import { fetchAgGridLicense } from '@/services/license';
import { LicenseManager } from 'ag-grid-enterprise';
import { useEffect } from 'react';

// Module-level sentinel stored on globalThis so it survives React StrictMode's
// mount→unmount→remount cycle in development and is reset between test runs.
const INIT_KEY = '__agGridLicenseInitialized__';

/**
 * Hook that initializes the AG Grid license with a fallback strategy:
 *
 * **Local Development (import.meta.env.MODE === 'development'):**
 * - If `VITE_AG_GRID_LICENSE` is set in `.env`, uses it directly (no API call)
 * - If not set, fetches from the API endpoint
 *
 * **Production (import.meta.env.MODE === 'production'):**
 * - Always fetches from the API endpoint
 *
 * The license is initialized at most once per page load (guarded by a globalThis
 * sentinel). If fetching fails, a warning is logged to console but the app
 * continues (grids may show license warnings).
 *
 * @example
 * ```typescript
 * function App() {
 *   useAgGridLicense();
 *   return <RouterProvider {...} />;
 * }
 * ```
 */
export function useAgGridLicense(): void {
  useEffect(() => {
    const g = globalThis as Record<string, unknown>;

    // Ensure this runs only once per page load, even under StrictMode
    if (g[INIT_KEY]) {
      return;
    }
    g[INIT_KEY] = true;

    async function initializeLicense() {
      try {
        const isDevelopment = import.meta.env.MODE === 'development';
        const envLicense = import.meta.env.VITE_AG_GRID_LICENSE;

        // Local development: use .env if available, otherwise fetch from API
        if (isDevelopment && envLicense) {
          LicenseManager.setLicenseKey(envLicense);
          return;
        }

        // Production or local without .env: fetch from API
        const apiLicense = await fetchAgGridLicense();
        LicenseManager.setLicenseKey(apiLicense);
      } catch (error) {
        console.warn(
          'Failed to initialize AG Grid license. Grids may display license warning.',
          error instanceof Error ? error.message : error,
        );
        // Do not re-throw; allow app to continue
      }
    }

    void initializeLicense();
  }, []);
}
